import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, CalendarClock, GraduationCap, Users, Briefcase, ChevronRight, Loader2,
} from "lucide-react";
import { api } from "../../lib/api";
import { WorkspaceShell, WorkspaceSupportBlock } from "../../components/workspace/WorkspaceShell";
import PccUpgradePromo from "../../components/carer-essentials/PccUpgradePromo";

const SECTIONS = [
  { slug: "documents", title: "My Documents", icon: FileText, body: "DBS, insurance, licences, certificates and other professional documents.", countKey: "documents_count", countLabel: "documents" },
  { slug: "expiries", title: "Expiries & Renewals", icon: CalendarClock, body: "A single at-a-glance view of everything expiring or expired.", countKey: "expiring_soon_count", countLabel: "expiring soon" },
  { slug: "cpd", title: "Training & CPD", icon: GraduationCap, body: "A private record of training completed anywhere — not just through JCC.", countKey: "cpd_entries_count", countLabel: "entries" },
  { slug: "references", title: "References", icon: Users, body: "A private organisational record of referees.", countKey: "references_count", countLabel: "references" },
  { slug: "work-records", title: "Work Record", icon: Briefcase, body: "A private record of your placements and work history.", countKey: "work_records_count", countLabel: "records" },
];

// Trims trailing zeroes from decimal CPD hours (18.50 -> 18.5, 12.00 -> 12).
function formatHours(n) {
  const num = Number(n) || 0;
  return Number(num.toFixed(1)).toString();
}

export default function CarerEssentialsHub() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/api/carer-essentials/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary({}));
  }, []);

  const hasAlert = summary && (summary.expired_count > 0 || summary.expiring_soon_count > 0);

  return (
    <WorkspaceShell
      testId="hub-carer-essentials"
      eyebrow="Carer Essentials"
      memberBadge="Carer Essentials member"
      accent="teal"
      title="Your Professional Admin Workspace"
      intro="Keep your documents, training, references and work history organised and private — never shown to families, never part of any work-finding feature."
    >
      {hasAlert && (
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-3"
          data-testid="ce-alert-banner"
        >
          <p className="text-sm text-amber-900">
            {summary.expired_count > 0 && <strong>{summary.expired_count} expired. </strong>}
            {summary.expiring_soon_count > 0 && <strong>{summary.expiring_soon_count} expiring within 30 days.</strong>}
          </p>
          <Link
            to="/workspace/carer-essentials/expiries"
            className="text-sm font-semibold text-amber-900 hover:underline inline-flex items-center gap-1"
            data-testid="ce-alert-link"
          >
            Review now <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {summary && (
        <div
          className="rounded-2xl border border-teal/20 bg-teal/5 p-5 flex items-center gap-4"
          data-testid="ce-cpd-hours-stat"
        >
          <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0" aria-hidden="true">
            <GraduationCap className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <p className="font-display text-2xl font-bold text-ink" data-testid="ce-cpd-hours-value">
              {formatHours(summary.cpd_hours_this_year)} hours
            </p>
            <p className="text-sm text-ink-muted" data-testid="ce-cpd-hours-label">
              CPD Hours This Year
              {summary.cpd_activities_this_year > 0
                ? ` · Across ${summary.cpd_activities_this_year} CPD ${summary.cpd_activities_this_year === 1 ? "activity" : "activities"}`
                : " · No CPD recorded yet this year"}
            </p>
          </div>
        </div>
      )}

      <section data-testid="ce-hub-sections">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            let countText = null;
            if (summary) {
              if (s.slug === "expiries") {
                const parts = [];
                if (summary.expired_count) parts.push(`${summary.expired_count} expired`);
                if (summary.expiring_soon_count) parts.push(`${summary.expiring_soon_count} expiring soon`);
                countText = parts.join(" · ") || null;
              } else {
                const count = summary[s.countKey];
                countText = count != null ? `${count} ${s.countLabel}` : null;
              }
            }
            return (
              <Link
                key={s.slug}
                to={`/workspace/carer-essentials/${s.slug}`}
                data-testid={`ce-hub-card-${s.slug}`}
                className="rounded-2xl border border-teal/20 bg-white p-6 hover:shadow-md transition-shadow group min-w-0"
              >
                <Icon className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
                <h3 className="font-display font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{s.body}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-ink-soft" data-testid={`ce-hub-count-${s.slug}`}>
                    {summary === null ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                    ) : (
                      countText || "\u00A0"
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal">
                    Open <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <PccUpgradePromo />

      <WorkspaceSupportBlock />
    </WorkspaceShell>
  );
}
